import React, { useEffect, useState, useRef, useContext } from 'react';
import { BellIcon, SearchIcon, UserIcon, LogOutIcon, SettingsIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LanguageContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { buttonHover, buttonTap, fadeIn, slideUp } from '../utils/animations';
import { Tooltip } from './ui/Tooltip';
import { createPortal } from 'react-dom';
interface HeaderProps {
  setSidebarOpen?: (open: boolean) => void;
}
export const Header: React.FC<HeaderProps> = ({
  setSidebarOpen
}) => {
  const {
    language,
    direction
  } = useContext(LanguageContext);
  const {
    user,
    logout
  } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    right: 0
  });
  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation(); // Stop event propagation
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right
      });
    }
    setMenuOpen(!menuOpen);
  };
  const closeMenu = () => {
    setMenuOpen(false);
  };
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only process if menu is open
      if (!menuOpen) return;
      // Check if click is outside both button and menu
      if (menuButtonRef.current && !menuButtonRef.current.contains(event.target as Node) && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    // Add event listener with capture to catch events before they reach components
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [menuOpen]);
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.name) return 'A';
    const nameParts = user.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };
  return <motion.header className="bg-white border-b border-gray-200 py-4 px-4 md:px-6 sticky top-0 z-50" initial={{
    y: -20,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.3
  }} data-id="element-604">
      <div className="flex items-center justify-between" data-id="element-605">
        <motion.div className="flex items-center" variants={slideUp} initial="hidden" animate="visible" data-id="element-606">
          <h1 className="text-xl font-semibold text-gray-800" data-id="element-607">
            {language === 'en' ? 'Admin Dashboard' : 'لوحة التحكم الإدارية'}
          </h1>
        </motion.div>
        <motion.div className="flex items-center space-x-2 md:space-x-4" variants={fadeIn} initial="hidden" animate="visible" data-id="element-608">
          <motion.div className="relative hidden md:block" initial={{
          width: '90%'
        }} animate={{
          width: '100%'
        }} transition={{
          duration: 0.3
        }} data-id="element-609">
            <motion.input type="text" placeholder={language === 'en' ? 'Search...' : 'بحث...'} className="w-64 px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} animate={{
            boxShadow: searchFocused ? '0 0 0 3px rgba(0, 151, 255, 0.2)' : '0 0 0 0 rgba(0, 151, 255, 0)'
          }} data-id="element-610" />
            <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-611" />
          </motion.div>
          <motion.div className="relative" data-id="element-612">
            <Tooltip content={language === 'en' ? 'Notifications' : 'الإشعارات'} side="bottom" data-id="element-613">
              <motion.button className="p-2 rounded-full hover:bg-gray-100 relative" whileHover={buttonHover} whileTap={buttonTap} data-id="element-614">
                <BellIcon size={20} className="text-gray-600" data-id="element-615" />
                <motion.span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-616"></motion.span>
              </motion.button>
            </Tooltip>
          </motion.div>
          <LanguageSwitcher data-id="element-617" />
          <div className="relative" data-id="element-618">
            <Tooltip content={language === 'en' ? 'User Menu' : 'قائمة المستخدم'} side="bottom" data-id="element-619">
              <motion.button ref={menuButtonRef} onClick={toggleMenu} className="flex items-center space-x-1 p-1 rounded-md hover:bg-gray-100 relative" whileHover={buttonHover} whileTap={buttonTap} data-id="element-620">
                <motion.div className="w-8 h-8 rounded-full bg-congress-blue-600 text-white flex items-center justify-center font-medium" whileHover={{
                scale: 1.05
              }} data-id="element-621">
                  {getUserInitials()}
                </motion.div>
                <span className="ml-2 text-sm font-medium text-gray-700 hidden md:inline" data-id="element-622">
                  {user?.name || 'Admin'}
                </span>
                <motion.div animate={{
                rotate: menuOpen ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-623">
                  <ChevronDownIcon size={16} className="text-gray-500 hidden md:inline" data-id="element-624" />
                </motion.div>
              </motion.button>
            </Tooltip>
            <AnimatePresence data-id="element-625">
              {menuOpen && createPortal(<motion.div ref={menuRef} className="fixed z-[9999] bg-white rounded-md shadow-lg py-1 border border-gray-200" variants={fadeIn} initial="hidden" animate="visible" exit="exit" style={{
              top: `${menuPosition.top}px`,
              [direction === 'rtl' ? 'left' : 'right']: `${menuPosition.right}px`,
              width: '12rem'
            }} onClick={e => e.stopPropagation()} data-id="element-626">
                    <motion.button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" variants={slideUp} whileHover={{
                backgroundColor: 'rgba(243, 244, 246, 1)'
              }} data-id="element-627">
                      <UserIcon size={16} className="mr-2" data-id="element-628" />
                      {language === 'en' ? 'My Profile' : 'الملف الشخصي'}
                    </motion.button>
                    <motion.button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" variants={slideUp} whileHover={{
                backgroundColor: 'rgba(243, 244, 246, 1)'
              }} data-id="element-629">
                      <SettingsIcon size={16} className="mr-2" data-id="element-630" />
                      {language === 'en' ? 'Account Settings' : 'إعدادات الحساب'}
                    </motion.button>
                    <motion.button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" variants={slideUp} whileHover={{
                backgroundColor: 'rgba(243, 244, 246, 1)'
              }} data-id="element-631">
                      <HelpCircleIcon size={16} className="mr-2" data-id="element-632" />
                      {language === 'en' ? 'Help & Support' : 'المساعدة والدعم'}
                    </motion.button>
                    <motion.div className="border-t border-gray-100 my-1" variants={slideUp} data-id="element-633"></motion.div>
                    <motion.button onClick={logout} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100" variants={slideUp} whileHover={{
                backgroundColor: 'rgba(243, 244, 246, 1)'
              }} data-id="element-634">
                      <LogOutIcon size={16} className="mr-2" data-id="element-635" />
                      {language === 'en' ? 'Sign Out' : 'تسجيل الخروج'}
                    </motion.button>
                  </motion.div>, document.body)}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </motion.header>;
};